import * as echarts from "echarts";

import { formatNumber } from "@formant/core/common/formatNumber";
import { AnalyticsChartType } from "@formant/core/model/AnalyticsChartType";

function getComputedCssVariable(variable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(
        variable,
    );
}

const colors = [
    "--formant-color-data-royal-blue",
    "--formant-color-data-red",
    "--formant-color-data-purple",
    "--formant-color-data-orange",
    "--formant-color-data-yellow",
];

const safelyIndexColor = (i: number): string => {
    return getComputedCssVariable(
        colors[((i % colors.length) + colors.length) % colors.length],
    );
};

const getColor = (opacity: number, colorIndex: number) => {
    const rgb = safelyIndexColor(colorIndex);
    return (
        "rgba(" +
        parseInt(rgb.substring(1, 3), 16) +
        "," +
        parseInt(rgb.substring(3, 5), 16) +
        "," +
        parseInt(rgb.substring(5, 7), 16) +
        "," +
        opacity +
        ")"
    );
};

// eslint-disable-next-line
export const getItemStyle = (
    chartType: AnalyticsChartType,
    colorIndex: number,
): any => {
    if (chartType !== "bar") {
        return {
            color: getColor(1, colorIndex),
        };
    }
    return {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
                offset: 0,
                color: getColor(1, colorIndex),
            },
            {
                offset: 0.02,
                color: getColor(0.4, colorIndex),
            },
            {
                offset: 1,
                color: getColor(0, colorIndex),
            },
        ]),
    };
};

export const getAreaStyle = (i: number): any => {
    return {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
                offset: 0,
                color: getColor(1, i),
            },
            {
                offset: 1,
                color: getColor(0, i),
            },
        ]),
    };
};

export const basicConfig = {
    backgroundColor: getComputedCssVariable("--formant-color-primary-module"),
    textStyle: {
        fontFamily: "Moderat",
    },
    color: [
        getComputedCssVariable("--formant-color-data-royal-blue"),
        getComputedCssVariable("--formant-color-data-red"),
        getComputedCssVariable("--formant-color-data-purple"),
        getComputedCssVariable("--formant-color-data-orange"),
        getComputedCssVariable("--formant-color-data-yellow"),
    ],
    grid: {
        containLabel: true,
    },
};

export const legendConfig = {
    orient: "horizontal",
    itemGap: 30,
    pageTextStyle: {
        color: getComputedCssVariable("--formant-color-primary-silver"),
    },
    pageIconColor: getComputedCssVariable("--formant-color-primary-dark"),
    pageIconInactiveColor: getComputedCssVariable(
        "--formant-color-primary-black",
    ),
    textStyle: {
        width: 100,
        overflow: "truncate",
    },
};

export const tooltipConfig = {
    backgroundColor: getComputedCssVariable("--formant-color-primary-black"),
    borderColor: getComputedCssVariable("--formant-color-primary-black"),
    valueFormatter: (value: number): string =>
        formatNumber(value, undefined, 3, undefined),
    padding: [20, 16],
    textStyle: {
        color: getComputedCssVariable("--formant-color-primary-white"),
        fontSize: 13,
        fontWeight: 400,
        lineHeight: 20,
        letterSpacing: 0.6,
        textAlign: "left",
    },
    trigger: "item",
    axisPointer: {
        type: "cross",
        label: {
            backgroundColor: "#6a7985", // what color should this be?
        },
    },
};

export const basicSeriesConfig = {
    barGap: "10%",
    barCategoryGap: "10%",
    smooth: true,
    sampling: "lttb",
    showSymbol: false,
    dataZoom: [
        {
            type: "inside",
        },
    ],
    axisLine: {
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    splitLine: {
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    labelLine: {
        show: true,
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    emphasis: {
        focus: "series",
    },
};

export const xAxisConfig = {
    nameLocation: "middle",
    nameGap: 30,
    axisLabel: {
        hideOverlap: true,
    },
    splitLine: {
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    axisLine: {
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    nameTextStyle: {
        size: 10,
        fontWeight: 900,
        lineHeight: 18,
    },
};

export const yAxisConfig = {
    type: "value",
    nameLocation: "middle",
    axisLine: {
        show: true,
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    splitLine: {
        lineStyle: {
            color: getComputedCssVariable("--formant-color-primary-flagship"),
        },
    },
    nameTextStyle: {
        size: 10,
        fontWeight: 900,
        lineHeight: 18,
    },
    nameGap: 40,
};
